/**
 * Carries the centrally managed REST-vs-GraphQL layers onto the flag store, and
 * publishes the caller's plan ceiling to the tree.
 *
 * Two halves of one answer, deliberately kept apart because they are not the
 * same KIND of thing:
 *
 *   - The PREFERENCE layers (plan-published default › workspace default › the
 *     user's own choice) resolve in `app/dashboardPrefs` and are pushed here
 *     onto `FeatureFlags.setPolicy`. That is all the wiring central management
 *     needs: every adapter in the app — and inside `tx-table` / `tx-graph` —
 *     already resolves through `resolveTransport` over these very flags, so a
 *     default an admin publishes lands everywhere with no per-feature change.
 *     A `?debug=1` override still wins, which is the point of a diagnostic.
 *
 *   - The plan CEILING (`transportCapabilitiesForPlan`) is not a preference and
 *     is not pushed into the flags at all. It can only ever force REST, so
 *     expressing it as a flag value would make it indistinguishable from a
 *     choice someone made — and would let the debug override punch through a
 *     packaging boundary. It rides `<TransportCapabilitiesProvider>`, which
 *     `resolveTransport` consults as a separate input.
 *
 * Mounted INSIDE `<DashboardPrefsProvider>` (it reads that store) and inside
 * `<FlagsProvider>` (it writes that store). Renders its children untouched.
 *
 * On the bootstrap order: the policy layer is empty for the first frames, so the
 * transport registry's own defaults answer until the prefs cache seeds
 * (synchronously, from `ScopedStorage`) and the server row lands. That is the
 * correct failure mode — an unreachable preferences API costs a customisation,
 * never a read — and it is why the registry keeps real defaults rather than
 * deferring to whatever the network says.
 */
import * as React from "react";
import { useFeatureFlags } from "@authe/ui-core/flags";
import { TransportCapabilitiesProvider } from "@authe/ui-core/api";
import { useQueryClient } from "@tanstack/react-query";
import type { TransportCapabilitySource } from "@authe/fe-commons/transport";
import { transportCapabilitiesForPlan } from "@/config";
import { useDashboardPrefs } from "./dashboardPrefs";
import { useCurrentPlanId } from "./currentPlan";

export function TransportPolicyProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const flags = useFeatureFlags();
  const queryClient = useQueryClient();
  const { transportPolicy } = useDashboardPrefs();
  const planId = useCurrentPlanId();

  // Identity-stable when the plan is, so the context below doesn't re-render the
  // app on every prefs write.
  const capabilities = React.useMemo<TransportCapabilitySource>(
    () => transportCapabilitiesForPlan(planId),
    [planId],
  );

  // Serialised with SORTED keys so the effect fires on a change of meaning, not
  // on a re-render that rebuilt an equal object in a different order. Both the
  // dependency and the change-detection below read this one string.
  const serialised = React.useMemo(
    () =>
      JSON.stringify(
        Object.fromEntries(Object.entries(transportPolicy).sort(([a], [b]) => a.localeCompare(b))),
      ),
    [transportPolicy],
  );

  // What we last pushed. The FIRST push is the stored policy arriving, which is
  // nobody changing their mind — invalidating there would refetch the whole app
  // once per page load, for a policy the very first reads already used.
  const pushed = React.useRef<string | null>(null);

  React.useEffect(() => {
    const first = pushed.current === null;
    const changed = !first && pushed.current !== serialised;
    pushed.current = serialised;
    flags.setPolicy(JSON.parse(serialised) as Record<string, boolean>);
    // A react-query entry already in cache was fetched over the OLD transport and
    // nothing about its key changed, so without this a workspace default only
    // reaches the screens the reader navigates to next. Same reasoning as the
    // debug panel's invalidate.
    if (changed) void queryClient.invalidateQueries();
  }, [flags, serialised, queryClient]);

  return (
    <TransportCapabilitiesProvider capabilities={capabilities}>
      {children}
    </TransportCapabilitiesProvider>
  );
}
